import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { chime } from '../lib/chime';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, Sparkles, Loader2, Trash2, Download, Image as ImageIcon,
  Wand2, Package, Globe2, MessageSquare, Store,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = [
  { key: 'auto', label: 'Auto' },
  { key: 'realistic', label: 'Realistic' },
  { key: 'toon', label: 'Toon' },
  { key: 'pixel', label: 'Pixel' },
];
const TYPES = [
  { key: 'sprite', label: 'Sprite' },
  { key: 'texture', label: 'Texture' },
  { key: 'model', label: '3D Concept' },
];

export default function Assets() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('auto');
  const [type, setType] = useState('sprite');
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(null);

  // Batch pack
  const [pack, setPack] = useState({ theme: '', count: 4, style: 'auto', busy: false, open: false });
  const [remixingId, setRemixingId] = useState(null);
  const [publishingId, setPublishingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/assets/mine`, { withCredentials: true });
      setAssets(r.data?.assets || []);
    } catch {
      toast.error('Failed to load your asset library');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!prompt.trim() || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/assets/generate`, { prompt: prompt.trim(), style, type }, { withCredentials: true });
      setAssets(prev => [r.data.asset, ...prev]);
      setRemaining(r.data.remaining);
      setPrompt('');
      toast.success('Asset generated');
      chime('success');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this asset?')) return;
    try {
      await axios.delete(`${API}/assets/${id}`, { withCredentials: true });
      setAssets(prev => prev.filter(a => a.asset_id !== id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const remix = async (a) => {
    if (remixingId) return;
    setRemixingId(a.asset_id);
    try {
      const r = await axios.post(`${API}/assets/${a.asset_id}/remix`, {}, { withCredentials: true });
      setAssets(prev => [r.data.asset, ...prev]);
      setRemaining(r.data.remaining);
      toast.success('Remix ready');
      chime('success');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remix failed');
    } finally { setRemixingId(null); }
  };

  const togglePublish = async (a) => {
    if (publishingId) return;
    setPublishingId(a.asset_id);
    const path = a.marketplace ? 'unpublish' : 'publish';
    try {
      await axios.post(`${API}/assets/${a.asset_id}/${path}`, {}, { withCredentials: true });
      setAssets(prev => prev.map(x => x.asset_id === a.asset_id ? { ...x, marketplace: !a.marketplace } : x));
      toast.success(a.marketplace ? 'Unpublished' : 'Published to marketplace');
      chime('success');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setPublishingId(null); }
  };

  const runBatch = async () => {
    if (!pack.theme.trim() || pack.busy) return;
    setPack(p => ({ ...p, busy: true }));
    try {
      const r = await axios.post(`${API}/assets/batch`,
        { theme: pack.theme.trim(), count: pack.count, style: pack.style },
        { withCredentials: true }
      );
      const created = r.data?.assets || [];
      setAssets(prev => [...created, ...prev]);
      setRemaining(r.data?.remaining);
      if (r.data?.failed?.length) {
        toast.warning(`${created.length} generated · ${r.data.failed.length} failed`);
      } else {
        toast.success(`Pack ready — ${created.length} assets`);
      }
      setPack(p => ({ ...p, theme: '', open: false }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Batch failed');
    } finally {
      setPack(p => ({ ...p, busy: false }));
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="assets-page">
      <header className="border-b border-[#1E1E24] bg-[#0A0A0B]">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/dashboard" className="text-zinc-400 hover:text-white flex items-center gap-2 text-sm">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <div className="flex-1" />
          <Link to="/chat" className="text-xs font-mono text-zinc-400 hover:text-[#06B6D4] flex items-center gap-1" data-testid="assets-chat-link">
            <MessageSquare className="w-3.5 h-3.5" /> CHAT
          </Link>
          <Link to="/marketplace" className="text-xs font-mono text-zinc-400 hover:text-[#06B6D4] flex items-center gap-1" data-testid="assets-marketplace-link">
            <Store className="w-3.5 h-3.5" /> MARKETPLACE
          </Link>
          <div className="flex items-center gap-2 text-sm text-zinc-400 ml-3">
            <ImageIcon className="w-4 h-4 text-[#06B6D4]" />
            <span className="font-mono text-xs">{assets.length} ASSETS</span>
            {remaining !== null && <span className="ml-2 text-[10px] font-mono text-zinc-500">· {remaining} left today</span>}
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8 space-y-6">
        {/* Generator */}
        <section className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5" data-testid="asset-generator">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-[#06B6D4]" />
            <h2 className="text-lg font-bold">Generate a Game Asset</h2>
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPack(p => ({ ...p, open: !p.open }))}
              className="text-xs text-[#06B6D4] hover:bg-[#06B6D4]/10"
              data-testid="assets-batch-toggle"
            >
              <Package className="w-3.5 h-3.5 mr-1" /> Batch pack
            </Button>
          </div>
          <p className="text-xs text-zinc-500 mb-4">
            RPG-grade prompts. Try:
            <em className="text-zinc-400"> &ldquo;brave paladin knight with cyan armor&rdquo; · &ldquo;seamless mossy dungeon stone&rdquo; · &ldquo;elven ranger hero&rdquo;</em>
          </p>

          <div className="grid md:grid-cols-[1fr_140px_140px_auto] gap-2">
            <Input
              placeholder="Describe the asset you want…"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && generate()}
              className="bg-transparent border-[#1E1E24] text-sm"
              data-testid="asset-prompt"
              disabled={busy}
            />
            <select value={style} onChange={(e) => setStyle(e.target.value)} className="bg-[#121214] border border-[#1E1E24] rounded-md px-2 text-sm text-white" data-testid="asset-style" disabled={busy}>
              {STYLES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <select value={type} onChange={(e) => setType(e.target.value)} className="bg-[#121214] border border-[#1E1E24] rounded-md px-2 text-sm text-white" data-testid="asset-type" disabled={busy}>
              {TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <Button onClick={generate} disabled={busy || !prompt.trim()} className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90" data-testid="asset-generate">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate'}
            </Button>
          </div>

          {pack.open && (
            <div className="mt-4 pt-4 border-t border-[#1E1E24]" data-testid="assets-batch-panel">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-3.5 h-3.5 text-[#06B6D4]" />
                <span className="text-sm font-semibold">Batch Generate Pack</span>
                <span className="text-[10px] text-zinc-500 font-mono">One theme → up to 6 assets in parallel</span>
              </div>
              <div className="grid md:grid-cols-[1fr_100px_140px_auto] gap-2">
                <Input
                  placeholder="Theme, e.g. 'dark forest RPG'"
                  value={pack.theme}
                  onChange={(e) => setPack(p => ({ ...p, theme: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && runBatch()}
                  className="bg-transparent border-[#1E1E24] text-sm"
                  data-testid="assets-batch-theme"
                  disabled={pack.busy}
                />
                <select
                  value={pack.count}
                  onChange={(e) => setPack(p => ({ ...p, count: parseInt(e.target.value, 10) }))}
                  className="bg-[#121214] border border-[#1E1E24] rounded-md px-2 text-sm text-white"
                  disabled={pack.busy}
                  data-testid="assets-batch-count"
                >
                  {[2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n} items</option>)}
                </select>
                <select
                  value={pack.style}
                  onChange={(e) => setPack(p => ({ ...p, style: e.target.value }))}
                  className="bg-[#121214] border border-[#1E1E24] rounded-md px-2 text-sm text-white"
                  disabled={pack.busy}
                >
                  {STYLES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <Button
                  onClick={runBatch}
                  disabled={pack.busy || !pack.theme.trim()}
                  className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90"
                  data-testid="assets-batch-generate"
                >
                  {pack.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Generate Pack</>}
                </Button>
              </div>
            </div>
          )}
        </section>

        {/* Library */}
        <section>
          <h3 className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Your Library</h3>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-[#06B6D4]" />
            </div>
          ) : assets.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-[#1E1E24] rounded-lg" data-testid="assets-empty">
              <ImageIcon className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-sm text-zinc-500">No assets yet. Generate one above.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="assets-grid">
              {assets.map(a => (
                <div
                  key={a.asset_id}
                  className="bg-[#0A0A0C] border border-[#1E1E24] rounded-lg overflow-hidden group hover:border-[#06B6D4]/50 transition-colors"
                  data-testid={`asset-card-${a.asset_id}`}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('application/x-playmold-asset', JSON.stringify({
                      asset_id: a.asset_id, url: a.url, name: a.name, asset_type: a.asset_type,
                    }));
                    e.dataTransfer.effectAllowed = 'copy';
                  }}
                >
                  <div className="aspect-square bg-[#121214] relative overflow-hidden">
                    <img src={a.url} alt={a.prompt} className="w-full h-full object-cover" loading="lazy" />
                    {a.marketplace && (
                      <span className="absolute top-2 left-2 text-[9px] font-mono bg-[#06B6D4] text-black px-1.5 py-0.5 rounded uppercase" data-testid={`asset-published-${a.asset_id}`}>
                        Published
                      </span>
                    )}
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <a href={a.url} download target="_blank" rel="noreferrer" className="w-7 h-7 rounded-md bg-black/70 backdrop-blur flex items-center justify-center text-white hover:text-[#06B6D4]" title="Download">
                        <Download className="w-3.5 h-3.5" />
                      </a>
                      <button onClick={() => remove(a.asset_id)} className="w-7 h-7 rounded-md bg-black/70 backdrop-blur flex items-center justify-center text-white hover:text-red-400" data-testid={`asset-delete-${a.asset_id}`} title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="p-3">
                    <input
                      type="text"
                      defaultValue={a.name || ''}
                      onBlur={async (e) => {
                        const nv = e.target.value.trim();
                        if (!nv || nv === a.name) return;
                        try {
                          await axios.patch(`${API}/assets/${a.asset_id}/name`, { name: nv }, { withCredentials: true });
                          setAssets(prev => prev.map(x => x.asset_id === a.asset_id ? { ...x, name: nv } : x));
                          toast.success('Renamed'); chime('success');
                        } catch (err) { toast.error(err.response?.data?.detail || 'Rename failed'); }
                      }}
                      className="w-full text-xs font-semibold bg-transparent text-white outline-none border-b border-transparent hover:border-[#1E1E24] focus:border-[#06B6D4] mb-1 px-0.5"
                      data-testid={`asset-name-${a.asset_id}`}
                    />
                    <p className="text-[11px] text-zinc-400 line-clamp-2 mb-2" title={a.prompt}>{a.prompt}</p>
                    <div className="flex items-center justify-between text-[10px] font-mono text-zinc-500 mb-2">
                      <span>{(a.asset_type || 'asset').toUpperCase()}</span>
                      <span>{(a.style || 'auto').toUpperCase()}</span>
                    </div>
                    {isAdmin && a.marketplace && (
                      <div className="flex items-center gap-1 mb-2">
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          defaultValue={a.price_usd || 0}
                          onBlur={async (e) => {
                            const price = parseFloat(e.target.value) || 0;
                            try {
                              await axios.post(`${API}/assets/${a.asset_id}/price`, { price_usd: price }, { withCredentials: true });
                              setAssets(prev => prev.map(x => x.asset_id === a.asset_id ? { ...x, price_usd: price || null } : x));
                              toast.success(price > 0 ? `Priced $${price.toFixed(2)}` : 'Set to free');
                              chime('success');
                            } catch (err) { toast.error(err.response?.data?.detail || 'Price update failed'); }
                          }}
                          className="w-16 h-6 text-[11px] bg-[#121214] border border-[#1E1E24] rounded px-1 text-white"
                          placeholder="0"
                          data-testid={`asset-price-${a.asset_id}`}
                        />
                        <span className="text-[10px] text-zinc-500 font-mono">USD (admin)</span>
                      </div>
                    )}
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="flex-1 h-7 text-[10px] border-[#1E1E24] hover:border-[#06B6D4] hover:text-[#06B6D4]" onClick={() => remix(a)} disabled={remixingId === a.asset_id} data-testid={`asset-remix-${a.asset_id}`}>
                        {remixingId === a.asset_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Wand2 className="w-3 h-3 mr-1" /> Remix</>}
                      </Button>
                      <Button size="sm" variant="outline" className={`flex-1 h-7 text-[10px] border-[#1E1E24] hover:border-[#06B6D4] hover:text-[#06B6D4] ${a.marketplace ? 'text-[#06B6D4]' : ''}`} onClick={() => togglePublish(a)} disabled={publishingId === a.asset_id} data-testid={`asset-publish-${a.asset_id}`}>
                        {publishingId === a.asset_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Globe2 className="w-3 h-3 mr-1" /> {a.marketplace ? 'Unpublish' : 'Publish'}</>}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
